import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
	Dialog,
	DialogContent,
	DialogTitle,
	Button,
	DialogActions,
	CircularProgress,
	TextField,
	Switch,
	FormControlLabel,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},

	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const SessionSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muito curto!")
		.max(50, "Muito longo!")
		.required("Obrigatório"),
});

const proxyStateOptions = [
	{ value: "", label: "Sem state especifico" },
	{ value: "state.acre", label: "Acre" },
	{ value: "state.alagoas", label: "Alagoas" },
	{ value: "state.amapa", label: "Amapa" },
	{ value: "state.amazonas", label: "Amazonas" },
	{ value: "state.bahia", label: "Bahia" },
	{ value: "state.ceara", label: "Ceara" },
	{ value: "state.distrito_federal", label: "Distrito Federal" },
	{ value: "state.espirito_santo", label: "Espirito Santo" },
	{ value: "state.goias", label: "Goias" },
	{ value: "state.maranhao", label: "Maranhao" },
	{ value: "state.mato_grosso", label: "Mato Grosso" },
	{ value: "state.mato_grosso_do_sul", label: "Mato Grosso do Sul" },
	{ value: "state.minas_gerais", label: "Minas Gerais" },
	{ value: "state.para", label: "Para" },
	{ value: "state.paraiba", label: "Paraiba" },
	{ value: "state.parana", label: "Parana" },
	{ value: "state.pernambuco", label: "Pernambuco" },
	{ value: "state.piaui", label: "Piaui" },
	{ value: "state.rio_de_janeiro", label: "Rio de Janeiro" },
	{ value: "state.rio_grande_do_norte", label: "Rio Grande do Norte" },
	{ value: "state.rio_grande_do_sul", label: "Rio Grande do Sul" },
	{ value: "state.rondonia", label: "Rondonia" },
	{ value: "state.roraima", label: "Roraima" },
	{ value: "state.santa_catarina", label: "Santa Catarina" },
	{ value: "state.sao_paulo", label: "Sao Paulo" },
	{ value: "state.sergipe", label: "Sergipe" },
	{ value: "state.tocantins", label: "Tocantins" },
];

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
	const classes = useStyles();
	const initialState = {
		name: "",
		greetingMessage: "",
		farewellMessage: "",
		isRestricted: false,
		isDefault: false,
		boot: false,
		proxyEnabled: false,
		proxyServer: "",
		proxyState: "",
	};
	const [whatsApp, setWhatsApp] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);

	useEffect(() => {
		const fetchSession = async () => {
			if (!whatsAppId) return;

			try {
				const { data } = await api.get(`whatsapp/${whatsAppId}`);
				setWhatsApp(data);

				const whatsQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(whatsQueueIds);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, [whatsAppId]);

	const handleSaveWhatsApp = async values => {
		const whatsappData = {
			...values,
			proxyServer: values.proxyEnabled ? values.proxyServer : "",
			proxyState: values.proxyEnabled ? values.proxyState : "",
			queueIds: selectedQueueIds,
		};

		try {
			if (whatsAppId) {
				await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
			} else {
				await api.post("/whatsapp", whatsappData);
			}
			toast.success(i18n.t("whatsappModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	const handleClose = () => {
		onClose();
		setWhatsApp(initialState);
		setSelectedQueueIds([]);
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth
				scroll="paper"
			>
				<DialogTitle>
					{whatsAppId
						? i18n.t("whatsappModal.title.edit")
						: i18n.t("whatsappModal.title.add")}
				</DialogTitle>
				<Formik
					initialValues={whatsApp}
					enableReinitialize={true}
					validationSchema={SessionSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveWhatsApp(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, touched, errors, isSubmitting, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("whatsappModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										className={classes.textField}
									/>
									<FormControlLabel
										control={
											<Field
												as={Switch}
												color="primary"
												name="isDefault"
												checked={values.isDefault}
											/>
										}
										label={i18n.t("whatsappModal.form.default")}
									/>
								</div>
								<div>
									<Field
										as={TextField}
										label={i18n.t("queueModal.form.greetingMessage")}
										type="greetingMessage"
										multiline
										rows={5}
										fullWidth
										name="greetingMessage"
										error={
											touched.greetingMessage && Boolean(errors.greetingMessage)
										}
										helperText={
											touched.greetingMessage && errors.greetingMessage
										}
										variant="outlined"
										margin="dense"
									/>
								</div>
								<div>
									<Field
										as={TextField}
										label={i18n.t("whatsappModal.form.farewellMessage")}
										type="farewellMessage"
										multiline
										rows={5}
										fullWidth
										name="farewellMessage"
										error={
											touched.farewellMessage && Boolean(errors.farewellMessage)
										}
										helperText={
											touched.farewellMessage && errors.farewellMessage
										}
										variant="outlined"
										margin="dense"
									/>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										rows={5}

										label={i18n.t("connections.table.color")}
										name="color"
										type="color"
										error={touched.color && Boolean(errors.color)}
										helperText={touched.color && errors.color}
										variant="outlined"
										margin="dense"
										className={classes.textField}
										fullWidth
										inputProps={{
											style: { padding: '4px', height: '40px' }
										}}
									/>
								</div>
								<QueueSelect
									selectedQueueIds={selectedQueueIds}
									onChange={selectedIds => setSelectedQueueIds(selectedIds)}
								/>

								<div className={classes.multFieldLine}>

									<FormControlLabel
										control={
											<Field
												as={Switch}
												color="primary"
												name="boot"
												checked={values.boot}
												onChange={(event) =>
													setFieldValue("boot", event.target.checked)
												}
											/>
										}
										label={i18n.t("whatsappModal.form.boot")}
									/>
									<FormControlLabel
										control={
											<Switch
												color="primary"
												checked={values.isRestricted}
												onChange={(event) =>
													setFieldValue("isRestricted", event.target.checked)
												}
											/>
										}
										label={i18n.t("connections.table.isRestricted")}
									/>
								</div>
								<div>
									<FormControlLabel
										control={
											<Switch
												color="primary"
												checked={values.proxyEnabled}
												onChange={(event) =>
													setFieldValue("proxyEnabled", event.target.checked)
												}
											/>
										}
										label="Usar proxy nesta instância"
									/>
								</div>
								{values.proxyEnabled && (
									<>
										<div>
											<Field
												as={TextField}
												label="PROXY_SERVER"
												name="proxyServer"
												placeholder="http://host:porta"
												fullWidth
												error={touched.proxyServer && Boolean(errors.proxyServer)}
												helperText={
													(touched.proxyServer && errors.proxyServer) ||
													"Exemplo: http://gw.dataimpulse.com:10000"
												}
												variant="outlined"
												margin="dense"
											/>
										</div>
										<FormControl
											variant="outlined"
											margin="dense"
											fullWidth
										>
											<InputLabel>State do proxy</InputLabel>
											<Select
												value={values.proxyState || ""}
												onChange={(event) =>
													setFieldValue("proxyState", event.target.value)
												}
												label="State do proxy"
											>
												{proxyStateOptions.map(option => (
													<MenuItem key={option.value || "empty"} value={option.value}>
														{option.label}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</>
								)}
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("whatsappModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{whatsAppId
										? i18n.t("whatsappModal.buttons.okEdit")
										: i18n.t("whatsappModal.buttons.okAdd")}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default React.memo(WhatsAppModal);
